<template>
  <!-- Modo botón (comportamiento original) -->
  <template v-if="!dropzone">
    <div class="relative flex justify-center">
      <input
        :id="inputId"
        v-bind="$attrs"
        type="file"
        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        @change="onChangeFile"
      />
      <label
        :for="inputId"
        class="block w-full max-w-2xs py-2 px-4 rounded-lg bg-blue-600 text-white font-semibold text-sm cursor-pointer text-center"
      >
        {{ label || 'Selecciona un archivo' }}
      </label>
      <XMarkIcon
        v-if="clear"
        style="right: -10px; top: -10px"
        class="cursor-pointer absolute shrink-0 size-6 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-50 text-gray-500 hover:bg-blue-100 disabled:opacity-50 disabled:pointer-events-none"
        @click="clearEvent ? clearEvent() : (model = null)"
      />
    </div>
    <p v-if="showSelectedFileName" class="text-sm text-gray-600 mt-2 dark:text-gray-300 text-center">
      {{ model?.name || 'Sin archivos seleccionados' }}
    </p>
  </template>

  <!-- Modo dropzone -->
  <template v-else>
    <div
      :class="[
        'relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer',
        isDragging
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-slate-700'
      ]"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      @click="dropzoneInputRef?.click()"
    >
      <input
        ref="dropzoneInputRef"
        v-bind="$attrs"
        type="file"
        class="hidden"
        @change="onChangeFile"
      />

      <div v-if="!model" class="text-center">
        <ArrowUpTrayIcon class="size-10 text-gray-400 mx-auto mb-3" />
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
          Arrastra el archivo aquí o
          <span class="text-blue-600 dark:text-blue-400">haz clic para seleccionar</span>
        </p>
        <p v-if="dropzoneDescription" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {{ dropzoneDescription }}
        </p>
      </div>

      <div v-else class="text-center">
        <DocumentIcon class="size-10 text-blue-600 mx-auto mb-3" />
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ model.name }}</p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatFileSize(model.size) }}</p>
        <button
          class="mt-2 text-xs text-red-500 hover:text-red-700 dark:text-red-400"
          @click.stop="model = null"
        >
          Quitar archivo
        </button>
      </div>
    </div>
  </template>
</template>


<script setup lang="ts">
import { ref, computed } from 'vue'
import { XMarkIcon, ArrowUpTrayIcon, DocumentIcon } from '@heroicons/vue/24/outline'

const model = defineModel<File | null>()

const props = defineProps({
  label: String,
  clear: {
    type: Boolean,
    default: false
  },
  clearEvent: Function,
  error: {
    type: String,
    default: null
  },
  showSelectedFileName: {
    type: Boolean,
    default: true
  },
  dropzone: {
    type: Boolean,
    default: false
  },
  dropzoneDescription: {
    type: String,
    default: null
  }
})

const dropzoneInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)

const inputId = computed(() => props.label ?? `file-input-${Math.random().toString(36).slice(2)}`)

const onChangeFile = (event: Event) => {
  const files = (event.target as HTMLInputElement).files
  model.value = files && files.length > 0 ? files[0] : null
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) model.value = file
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>
